import type { BeatEvent, BeatMap, Line, RevisionContent, Section } from '../schema/v1'
import { meterOf } from './meter'

/**
 * Mapa de tiempos (§5.4): cuánto dura cada acorde de cada aparición, en pulsos. Una hoja de
 * acordes no lo dice, así que se parte de una estimación por regla y se afina grabando los
 * cambios o a mano. Todo apunta a IDs de aparición y acorde, nunca a la pantalla (regla 3).
 */

/** Reglas de estimación: un compás por acorde, o dos o cuatro compases por línea. */
export const ESTIMATE_RULES = ['bar-per-chord', 'line-2-bars', 'line-4-bars'] as const
export type EstimateRule = (typeof ESTIMATE_RULES)[number]

export const DEFAULT_RULE: EstimateRule = 'bar-per-chord'

const MAX_BEATS = 256

const clampBeats = (beats: number) => Math.min(MAX_BEATS, Math.max(beats, 1 / 64))

/** Redondeo a milésimas: suficiente para un pulso y evita arrastrar ruido de coma flotante. */
export const roundBeats = (beats: number) => Math.round(beats * 1000) / 1000

function linesToBars(rule: EstimateRule): number {
  return rule === 'line-4-bars' ? 4 : rule === 'line-2-bars' ? 2 : 1
}

/**
 * Reparte `total` pulsos enteros según los pesos, con un pulso como mínimo por parte (resto
 * mayor). Si no caben, cada parte se queda con un pulso.
 */
export function splitBeats(total: number, weights: readonly number[]): number[] {
  const count = weights.length
  if (count === 0) return []
  if (count >= total) return weights.map(() => 1)
  const sum = weights.reduce((acc, weight) => acc + Math.max(weight, 0), 0)
  const remaining = total - count
  const exact = weights.map((weight) =>
    sum > 0 ? (Math.max(weight, 0) / sum) * remaining : remaining / count,
  )
  const base = exact.map(Math.floor)
  let left = remaining - base.reduce((acc, value) => acc + value, 0)
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index)
  for (const { index } of order) {
    if (left <= 0) break
    base[index] = base[index]! + 1
    left -= 1
  }
  return base.map((value) => value + 1)
}

/** Lo que ocupa cada acorde en el texto de su línea: hasta el siguiente acorde o el final. */
function chordSpans(line: Line): number[] {
  return line.chords.map((anchor, index) => {
    const end = line.chords[index + 1]?.at ?? line.text.length
    return Math.max(1, end - anchor.at)
  })
}

const isLyric = (line: Line) => line.kind !== 'comment' && line.text.trim() !== ''

/**
 * Reparte los compases de una línea entre sus acordes según lo que ocupa cada uno en el texto. Se
 * reparte en la unidad más musical que alcance: compases enteros si hay acordes de sobra, si no
 * medios compases y, como último recurso, pulsos.
 */
function splitLine(line: Line, bars: number, beatsPerBar: number): number[] {
  const count = line.chords.length
  const unit =
    count <= bars ? beatsPerBar : count <= bars * 2 && beatsPerBar % 2 === 0 ? beatsPerBar / 2 : 1
  return splitBeats((bars * beatsPerBar) / unit, chordSpans(line)).map((units) => units * unit)
}

/** Pulsos de cada acorde de una sección según la regla, y los que alargan al acorde anterior. */
function estimateSection(
  section: Section,
  rule: EstimateRule,
  beatsPerBar: number,
): { beats: number[]; leading: number } {
  const beats: number[] = []
  let leading = 0
  const extend = (amount: number) => {
    if (beats.length === 0) leading += amount
    else beats[beats.length - 1] = beats[beats.length - 1]! + amount
  }
  const lineBars = linesToBars(rule)
  for (const line of section.lines) {
    if (section.kind === 'tab' || line.kind === 'comment') continue
    if (line.chords.length === 0) {
      // Una línea cantada sin acordes: sigue sonando el anterior.
      if (isLyric(line)) extend(lineBars * beatsPerBar)
      continue
    }
    if (rule === 'bar-per-chord' || line.text.trim() === '') {
      // Las líneas de solo acordes (intros, instrumentales) van a compás por acorde.
      line.chords.forEach(() => beats.push(beatsPerBar))
      continue
    }
    beats.push(...splitLine(line, lineBars, beatsPerBar))
  }
  return { beats, leading }
}

/** Estima el mapa de tiempos con una regla. Las apariciones de una misma sección duran lo mismo. */
export function estimateBeatMap(
  content: RevisionContent,
  rule: EstimateRule = DEFAULT_RULE,
): BeatMap {
  const { beatsPerBar } = meterOf(content)
  const sections = new Map(content.sections.map((section) => [section.id, section]))
  const events: BeatEvent[] = []
  for (const occurrence of content.sequence) {
    const section = sections.get(occurrence.section)
    if (!section) continue
    const { beats, leading } = estimateSection(section, rule, beatsPerBar)
    const chords = section.lines.flatMap((line) => line.chords)
    if (leading > 0) {
      // La sección empieza cantando sobre el acorde que venía sonando.
      const previous = events[events.length - 1]
      if (previous) previous.beats = clampBeats(previous.beats + leading)
    }
    chords.forEach((anchor, index) => {
      events.push({
        kind: 'chord',
        occurrence: occurrence.id,
        chord: anchor.id,
        beats: clampBeats(beats[index] ?? beatsPerBar),
      })
    })
  }
  return { status: 'estimated', origin: `rule:${rule}`, events }
}

export interface CompletedBeatMap {
  beatMap: BeatMap
  /** Eventos que no estaban y se han rellenado. */
  filled: number
  /** Verdadero si el mapa no tenía nada utilizable y todo sale de la regla. */
  fromRule: boolean
}

/**
 * El mapa completo para tocar: un evento por cada acorde de cada aparición, en orden. Lo que falta
 * se rellena copiando otra aparición de la misma sección y, si no hay, con la regla. Lo que apunta
 * a algo que ya no existe se descarta. Si se rellena algo, el mapa pasa a «estimado».
 */
export function completeBeatMap(
  content: RevisionContent,
  rule: EstimateRule = DEFAULT_RULE,
): CompletedBeatMap {
  const estimate = estimateBeatMap(content, rule)
  const original = content.beatMap
  if (original.status === 'absent' || original.events.length === 0) {
    return { beatMap: estimate, filled: estimate.events.length, fromRule: true }
  }

  const byOccurrence = new Map<string, BeatEvent[]>()
  for (const event of original.events) {
    const list = byOccurrence.get(event.occurrence) ?? []
    list.push(event)
    byOccurrence.set(event.occurrence, list)
  }
  const sections = new Map(content.sections.map((section) => [section.id, section]))
  // Duración conocida de cada acorde de cada sección, por si otra aparición la necesita.
  const known = new Map<string, number>()
  for (const occurrence of content.sequence) {
    for (const event of byOccurrence.get(occurrence.id) ?? []) {
      if (event.kind !== 'chord') continue
      const key = `${occurrence.section}:${event.chord}`
      if (!known.has(key)) known.set(key, event.beats)
    }
  }
  const estimated = new Map(
    estimate.events.flatMap((event) =>
      event.kind === 'chord' ? [[`${event.occurrence}:${event.chord}`, event.beats] as const] : [],
    ),
  )

  const events: BeatEvent[] = []
  let filled = 0
  for (const occurrence of content.sequence) {
    const section = sections.get(occurrence.section)
    if (!section) continue
    const existing = [...(byOccurrence.get(occurrence.id) ?? [])]
    const used = new Set<BeatEvent>()
    for (const anchor of section.lines.flatMap((line) => line.chords)) {
      const position = existing.findIndex(
        (event) => !used.has(event) && event.kind === 'chord' && event.chord === anchor.id,
      )
      if (position >= 0) {
        // Los silencios que iban antes de este acorde, en su sitio.
        for (const event of existing.slice(0, position)) {
          if (event.kind === 'rest' && !used.has(event)) {
            events.push(event)
            used.add(event)
          }
        }
        events.push(existing[position]!)
        used.add(existing[position]!)
        continue
      }
      filled += 1
      const beats =
        known.get(`${occurrence.section}:${anchor.id}`) ??
        estimated.get(`${occurrence.id}:${anchor.id}`) ??
        meterOf(content).beatsPerBar
      events.push({ kind: 'chord', occurrence: occurrence.id, chord: anchor.id, beats })
    }
    for (const event of existing) {
      if (event.kind === 'rest' && !used.has(event)) events.push(event)
    }
  }

  const status = filled > 0 && original.status === 'reviewed' ? 'estimated' : original.status
  const beatMap: BeatMap = { status, events }
  if (original.origin) beatMap.origin = original.origin
  return { beatMap, filled, fromRule: false }
}

/** Un evento del mapa con su inicio, en pulsos desde el principio de la canción. */
export interface TimedEvent {
  /** Posición en `beatMap.events`. */
  index: number
  kind: BeatEvent['kind']
  occurrence: string
  /** El acorde, o null en un silencio. */
  chord: string | null
  start: number
  beats: number
}

export interface Timing {
  events: TimedEvent[]
  /** Duración total en pulsos. */
  total: number
}

export function timingOf(beatMap: BeatMap): Timing {
  let start = 0
  const events = beatMap.events.map((event, index): TimedEvent => {
    const timed: TimedEvent = {
      index,
      kind: event.kind,
      occurrence: event.occurrence,
      chord: event.kind === 'chord' ? event.chord : null,
      start,
      beats: event.beats,
    }
    start += event.beats
    return timed
  })
  return { events, total: start }
}

/**
 * El evento que suena en un pulso: el último que empieza en él o antes. Antes del principio es el
 * primero y, pasado el final, el último. −1 si no hay eventos.
 */
export function eventIndexAt(timing: Timing, beat: number): number {
  const { events } = timing
  if (events.length === 0) return -1
  let low = 0
  let high = events.length - 1
  // Un margen mínimo evita que 3,9999999 caiga en el acorde anterior al de 4.
  const target = beat + 1e-9
  while (low < high) {
    const middle = Math.ceil((low + high) / 2)
    if (events[middle]!.start <= target) low = middle
    else high = middle - 1
  }
  return low
}

/** Inicio de una aparición: el de su primer evento. */
export function occurrenceStart(timing: Timing, occurrenceId: string): number | undefined {
  return timing.events.find((event) => event.occurrence === occurrenceId)?.start
}

/** Fin de una aparición: el del último evento que le pertenece. */
export function occurrenceEnd(timing: Timing, occurrenceId: string): number | undefined {
  const last = [...timing.events].reverse().find((event) => event.occurrence === occurrenceId)
  return last ? last.start + last.beats : undefined
}

/** Cuánto suma cada aparición y si cuadra con el compás (§11: «mapa que cuadra con el compás»). */
export interface BarCheck {
  occurrence: string
  beats: number
  /** Pulsos que sobran o faltan para cerrar el último compás: 0 si cuadra. */
  remainder: number
}

export function checkBars(content: RevisionContent, beatMap: BeatMap): BarCheck[] {
  const { beatsPerBar } = meterOf(content)
  const sums = new Map<string, number>()
  for (const event of beatMap.events) {
    sums.set(event.occurrence, (sums.get(event.occurrence) ?? 0) + event.beats)
  }
  return content.sequence.flatMap((occurrence) => {
    const beats = sums.get(occurrence.id)
    if (beats === undefined) return []
    const remainder = roundBeats(beats % beatsPerBar)
    return [{ occurrence: occurrence.id, beats: roundBeats(beats), remainder }]
  })
}
